/**
 * Auth Callback Handler — Identity Frontend
 *
 * Handles OIDC callback at the root level.
 * When Auth0 redirects back with code & state parameters,
 * this component redirects to /identity where the AuthProvider can process it,
 * or to the migration link callback when the wizard tagged the sign-in.
 *
 * Detection is SYNCHRONOUS (in the render path) so that children
 * (e.g. HomePage) never flash while the redirect is pending.
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { isMigrationLinkCallback } from "../auth/migrationLink";
import { ROUTES } from "../routes/constants";

function isOidcCallback(pathname: string, search: string): boolean {
  if (pathname !== "/") return false;
  const params = new URLSearchParams(search);
  return (params.has("code") || params.has("error")) && params.has("state");
}

export function AuthCallbackHandler({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isCallback = isOidcCallback(location.pathname, location.search);

  useEffect(() => {
    if (!isCallback) return;

    if (import.meta.env.DEV) {
      console.log("[AuthCallbackHandler] OIDC callback detected at root");
    }

    // The wizard's provider must process its own callback: the sign-in state
    // lives only on the returned user, it is not kept in the user store
    const target = isMigrationLinkCallback(location.search)
      ? ROUTES.MIGRATION.LINK_CALLBACK
      : ROUTES.MY_IDENTITY;
    navigate(`${target}${location.search}`, { replace: true });
  }, [isCallback, location.search, navigate]);

  if (isCallback) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={40} />
        <Typography color="text.secondary">Signing in…</Typography>
      </Box>
    );
  }

  return <>{children}</>;
}

export default AuthCallbackHandler;
